import React, { useEffect, useState } from 'react';
import { checkUserSessionPass, initiateSessionPassPayment } from './utils/apiServices';

/** Prevents duplicate payment/API calls when React Strict Mode runs effects twice in dev. */
let lastAutoRunSearch = null;

function extractSessionData(result) {
    const body = result?.data;
    const inner = body?.data ?? body;
    return {
        validUser: inner?.validUser === true,
        as_sessional_pass: inner?.as_sessional_pass === true,
        as_plus: inner?.as_plus === true,
    };
}

/** Handles `{ data: { data: { redirectUrl } } }` from payment initiate response. */
function extractPaymentRedirectUrl(result) {
    const root = result?.data;
    const mid = root?.data ?? root;
    const payload = mid?.data ?? mid;
    const url = payload?.redirectUrl;
    return typeof url === 'string' && url.length > 0 ? url : null;
}

export default function SessionPassPage() {
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [messageKind, setMessageKind] = useState('info');

    const show = (text, kind = 'info') => {
        setMessage(text);
        setMessageKind(kind);
    };

    const runSessionPassFlow = async (trimmed, purpose) => {
        const effectivePurpose = (purpose || '').trim() || 'session_pass';
        setLoading(true);
        setMessage('');

        try {
            const res = await checkUserSessionPass(trimmed);
            const { validUser, as_sessional_pass, as_plus } = extractSessionData(res);

            if (!validUser) {
                show('User not found', 'error');
                return;
            }

            if (effectivePurpose === 'plus_pass') {
                if (as_plus) {
                    show('User already has plus pass', 'error');
                    return;
                }
            } else if (as_sessional_pass) {
                show('User already as sessional pass', 'error');
                return;
            }

            const payRes = await initiateSessionPassPayment(trimmed, effectivePurpose);
            const redirectUrl = extractPaymentRedirectUrl(payRes);
            if (redirectUrl) {
                window.location.assign(redirectUrl);
                return;
            }
            show('Payment started but no redirect URL was returned.', 'warn');
        } catch (err) {
            show(err?.error || 'Something went wrong', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const search = window.location.search;
        if (lastAutoRunSearch === search) return;

        const params = new URLSearchParams(search);
        const qid = params.get('id')?.trim();
        const purpose = params.get('purpose')?.trim();
        lastAutoRunSearch = search;

        if (!qid) {
            show('Add a user id to the URL, for example ?id=145x', 'warn');
            return;
        }

        runSessionPassFlow(qid, purpose);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per URL on mount
    }, []);

    return (
        <main className="session-pass">
            <h1>Session pass</h1>
            {loading ? <p className="session-pass__status">Please wait…</p> : null}
            {message ? (
                <p
                    className={`session-pass__msg session-pass__msg--${messageKind}`}
                    role="status"
                >
                    {message}
                </p>
            ) : null}
        </main>
    );
}
